<script setup>
import {
  computed, toRefs, watchEffect, inject,
} from 'vue';

import { faSpinnerThird } from '@awesome.me/kit-a54fb3a664/icons/classic/solid';

import Button from 'primevue/button';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import useSpacing from '@/composables/useSpacing';

import useShoppableService from '@/services/shoppable';
import { NODE_ENV } from '@/config/appConfig';
import { encodeAffiliateMeta } from '@/utils/affiliate';

const pageData = inject('page');
const affiliates = inject('affiliates');

const shoppableService = useShoppableService(pageData.value.owner);

const props = defineProps(['settings']);
const { settings } = toRefs(props);

const type = computed(() => settings.value.type);
const itemId = computed(() => settings.value.shoppableId);

const {
  data: productData,
  isFetching: isProductFetching,
  execute: getProductData,
} = shoppableService.getProduct(itemId, { immediate: false });

const collectionQuery = computed(() => ({
  ...(itemId.value > 0 ? { collectionId: itemId.value } : {}), // null itemId = all products
  limit: 25,
}));

const {
  data: collectionData,
  isFetching: isCollectionFetching,
  execute: getCollectionData,
} = shoppableService.getProducts(collectionQuery, { immediate: false, initialData: { products: [] } });

const isFetching = computed(() => isProductFetching.value || isCollectionFetching.value);

watchEffect(() => {
  if (!itemId.value) return;

  if (type.value === 'product') {
    getProductData();
  } else if (type.value === 'collection') {
    getCollectionData();
  }
});

const shoppableData = computed(() => {
  if (type.value === 'product') {
    return productData.value ? [productData.value] : [];
  }

  return collectionData.value?.products;
});

// element styles
const productLayout = computed(() => settings.value.layout);
const productBackground = computed(() => {
  if (settings?.value?.product?.backgroundType === 'image') {
    return `url(${settings?.value?.product?.image})`;
  }

  return settings?.value?.product?.backgroundColor ?? 'var(--body-bg-color)';
});
const productBorderRadius = computed(() => `${settings?.value?.product?.borderRadius ?? '0'}px`);
const { padding: productPadding } = useSpacing(settings, { paddingPath: 'product.padding' }, { defaultPadding: '0' });

const productImageBorderRadius = computed(() => `${settings?.value?.productImage?.borderRadius ?? '0'}px`);
const productImageHeight = computed(() => `${settings?.value?.productImage?.height ?? '20'}vh`);
const hasProductImageDropShadow = computed(() => settings?.value.productImage.shadow);

const titleTextAlign = computed(() => settings.value.title.align);
const titleTextFont = computed(() => settings.value.title.font ?? 'Barlow');
const titleTextColor = computed(() => settings.value.title.color);
const titleTextSize = computed(() => `${settings?.value?.title?.textSize ?? 'var(--title-text-size)'}px`);

const priceTextAlign = computed(() => settings.value.price.align);
const priceTextFont = computed(() => settings.value.price.font ?? 'Barlow');
const priceTextColor = computed(() => settings.value.price.color);
const priceTextSize = computed(() => `${settings?.value?.price?.textSize ?? 14}px`);
const rawPriceTextSize = computed(() => settings?.value?.price?.textSize ?? 14);
const showDiscountInfo = computed(() => settings?.value?.price?.showListAndDiscountPrice ?? true);
const listTextSize = computed(() => `${rawPriceTextSize.value * 0.5 ?? 14}px`);
const discountTextSize = computed(() => `${rawPriceTextSize.value * 0.67 ?? 14}px`);

const buttonText = computed(() => settings.value.button.text);
const buttonBackgroundColor = computed(() => settings?.value?.button?.backgroundColor ?? 'var(--button-bg-color)');
const buttonTextColor = computed(() => settings.value.button.color ?? 'var(--button-text-color)');
const buttonBorderRadius = computed(() => `${settings?.value?.button?.borderRadius ?? 'var(--button-border-radius)'}px`);
const buttonTextSize = computed(() => `${settings?.value?.button?.textSize ?? 'var(--button-text-size)'}px`);
const buttonFont = computed(() => settings.value.button.font ?? 'Barlow');

const getCurrency = (currency) => {
  switch (currency) {
    case 'USD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '';
  }
};

const amazonTrackingId = computed(() => affiliates.value?.amazon?.config?.trackingId);

const makeAffiliateUrl = (product) => {
  const newUrl = new URL(product.sourceUrl);

  if (product.source === 'amazon') {
    if (amazonTrackingId.value) {
      newUrl.searchParams.set('tag', amazonTrackingId.value);
    }
  } else if (product.source === 'fanatics') {
    const affiliateLinkData = {
      affiliate: 'fanatics',
      ownerId: pageData.value.owner,
      pageId: pageData.value.id,
      catalogId: product?.CatalogId,
      sku: product?.CatalogItemId,
      timestamp: new Date().getTime(),
      env: NODE_ENV,
    };
    const subId3 = encodeAffiliateMeta(affiliateLinkData);

    newUrl.searchParams.append('subId1', pageData.value.owner);
    newUrl.searchParams.append('subId2', `page:${pageData.value.id}`);
    newUrl.searchParams.append('subId3', subId3);
  }

  return newUrl.href || '';
};

const gridClasses = computed(() => {
  if (type.value === 'collection') {
    return {
      'grid grid-cols-2 sm:grid-cols-3 gap-8': productLayout.value === 'portrait',
      'grid grid-cols-1 sm:grid-cols-2 gap-8': productLayout.value === 'landscape',
    };
  }

  return 'max-w-xs mx-auto';
});

</script>

<template>
  <div v-if="isFetching" class="flex justify-center items-center">
    <FontAwesomeIcon
      :icon="faSpinnerThird"
      spin
      class="loader"
    />
  </div>
  <div
    v-show="itemId && !isFetching"
    class="element shoppable-element"
  >
    <div
      :class="gridClasses"
    >
      <div
        v-for="p in shoppableData"
        :key="p.id"
        class="flex flex-col shoppableItemContainer"
      >
        <div :class="`shoppableItem shoppableItem--${productLayout}`">
          <img
            :src="p.imageUrl"
            alt="product image"
            class="shoppableItem__image"
            :class="{
              'w-full mb-4': productLayout === 'portrait',
              'w-6/12': productLayout === 'landscape',
              'drop-shadow-md': hasProductImageDropShadow,
            }"
          />
          <div
            :class="{
              'flex-grow flex flex-col justify-end': productLayout === 'portrait',
              'p-1': productLayout === 'landscape',
            }"
          >
            <p
              v-if="settings.title.visible"
              class="shoppableItem__title"
            >{{ p.name.length > 80 ? `${p.name.slice(0, 80)}...` : p.name }}</p>
            <p
              v-if="settings.price.visible"
              class="shoppableItem__price mt-2"
            >
              <span
                v-if="showDiscountInfo
                  && p.source === 'amazon'
                  && p.sourceItem?.OffersV2?.Listings?.[0]?.Price?.Savings?.Percentage
                "
                class="shoppableItem__discountPercent text-red-500"
              >-{{ p.sourceItem?.OffersV2?.Listings?.[0]?.Price?.Savings?.Percentage }}%</span>
              <span>{{ getCurrency(p.currency) }}{{ p.price }}</span>
            </p>
            <p
              v-if="settings.price.visible
                && showDiscountInfo
                && p.source === 'amazon'
                && p.sourceItem?.OffersV2?.Listings?.[0]?.Price?.SavingBasis?.Money?.DisplayAmount
              "
              class="shoppableItem__discount mt-2"
            >
              List Price:<span class="line-through">{{ p.sourceItem?.OffersV2?.Listings?.[0]?.Price?.SavingBasis?.Money?.DisplayAmount }}</span>
            </p>
            <a
              :href="makeAffiliateUrl(p)"
              class="shoppableItem__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                v-if="settings.button.visible"
                class="shoppableItem__button mt-4 hover:drop-shadow-md"
                :label="buttonText"
                severity='primary'
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.shoppableItemContainer {
  position: relative;
  background: v-bind(productBackground);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: v-bind(productBorderRadius);
  padding: v-bind(productPadding);
  overflow: hidden;
}

.shoppableItem {
  display: flex;

  &--portrait {
    flex-direction: column;
    flex-grow: 1;
  }
  // portrait overrides
  &--portrait &__title {
    flex-grow: 1;
  }

  &--landscape {
    flex-direction: row;
    align-items: flex-end;
    flex-grow: 1;
    gap: 10px;
  }
  // landscape overrides
  &--landscape &__image {
    align-self: center;
  }

  &__image {
    object-fit: contain;
    margin-left: auto;
    margin-right: auto;
    aspect-ratio: 1;
    border-radius: v-bind(productImageBorderRadius);
    background-color: white;
    height: v-bind(productImageHeight);
  }

  &__title {
    text-align: v-bind(titleTextAlign);
    font-family: v-bind(titleTextFont);
    color: v-bind(titleTextColor);
    font-size: v-bind(titleTextSize);
  }

  &__discount {
    text-align: v-bind(priceTextAlign);
    font-family: v-bind(priceTextFont);
    color: v-bind(priceTextColor);
    font-size: v-bind(listTextSize);
  }

  &__discountPercent {
    font-size: v-bind(discountTextSize);
    vertical-align: middle;
    margin-right: 0.25rem;
  }

  &__price {
    text-align: v-bind(priceTextAlign);
    font-family: v-bind(priceTextFont);
    color: v-bind(priceTextColor);
    font-size: v-bind(priceTextSize);
    font-weight: bold;
  }

  &__link {
    color: v-bind(buttonTextColor);

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 0;
    }
  }

  &__button {
    width: 100%;
    background: v-bind(buttonBackgroundColor);
    color: v-bind(buttonTextColor);
    font-family: v-bind(buttonFont);
    border-radius: v-bind(buttonBorderRadius);
    font-size: v-bind(buttonTextSize);
    position: relative;
    z-index: 1; // hover "through" the link ::after above
  }
}

.loader {
  font-size: 3rem;
  color: #00B4CC;
}
</style>
